import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  Loader,
  ConfirmationPopup,
  GeneralButton,
  GeneralInput,
} from "../../../components";
import { RegisterPasswordRequest } from "../../../interface/IRegister";
import { resetUserDetails } from "../../../redux/reducers/registrationSlice";
import { resetUserState } from "../../../redux/reducers/userSlice";
import AuthLayout from "../../../Layout/AuthLayout";
import useRegisterPassword from "../../../api/auth/register/useRegisterPassword";
import { TFunction } from "i18next";
import { useToast } from "../../../Services/ToastContext";
import { useAppDispatch, useAppSelector } from "../../../hooks";

interface PasswordFormInputs {
  Password: string;
}

interface PasswordValidationState {
  isUpperCasePresent: boolean;
  isNumberPresent: boolean;
  isPasswordLongEnough: boolean;
}

const createPasswordStepSchema = (t: TFunction) =>
  z.object({
    Password: z
      .string()
      .min(8, { message: t("password_min_length") })
      .regex(/[A-Z]/, { message: t("password_uppercase") })
      .regex(/\d/, { message: t("password_number") }),
  });

const RegisterPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.registration.userDetails);

  const { showToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { mutate, isPending } = useRegisterPassword();

  useEffect(() => {
    if (!userDetails.Id) {
      navigate("/login", { replace: true });
    }
  }, [userDetails, navigate]);

  const validationSchema = useMemo(() => createPasswordStepSchema(t), [t]);

  const methods = useForm<PasswordFormInputs>({
    defaultValues: {
      Password: "",
    },
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    trigger,
  } = methods;

  const Password = watch("Password", "");

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  const onSubmit: SubmitHandler<PasswordFormInputs> = (data) => {
    if (!data.Password) {
      setError(true);
      setErrorMessage(t("password_required"));
      return;
    }

    const requestData: RegisterPasswordRequest = {
      Id: userDetails.Id,
      FullName: userDetails.FullName,
      Password: data.Password,
    };

    mutate(requestData, {
      onError: (error: any) => {
        showToast(error.response?.data?.ErrorMessage || error.message, "error");
      },
    });
  };

  const handleConfirmGoBack = () => {
    dispatch(resetUserDetails());
    dispatch(resetUserState());
    setIsModalOpen(false);
    navigate("/sign-up");
  };

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      setIsModalOpen(true);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      setIsModalOpen(true);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const validatePasswordChars: PasswordValidationState = {
    isUpperCasePresent: /[A-Z]/.test(Password),
    isNumberPresent: /\d/.test(Password),
    isPasswordLongEnough: Password.length >= 8,
  };

  const criteriaMetCount = Object.values(validatePasswordChars).filter(
    Boolean,
  ).length;

  const getLineColor = (index: number, totalMet: number): string => {
    if (index >= totalMet) {
      return "bg-primary-bg";
    }
    switch (totalMet) {
      case 1:
        return "bg-body-text";
      case 2:
        return "bg-orange";
      case 3:
        return "bg-successGreen";
      default:
        return "bg-primary-bg";
    }
  };

  const getFeedbackTextColor = (isMet: boolean, currentPassword: string) => {
    if (!currentPassword) return "text-body-text";
    return isMet ? "text-successGreen" : "text-error";
  };

  const totalLines = 3;

  const isPasswordValid = criteriaMetCount === totalLines;

  return (
    <FormProvider {...methods}>
      <AuthLayout
        title={t("create_new_password")}
        description={t("create_your_password_msg")}
        lottieImage={null}
      >
        <h2 id="register-password-title" className="sr-only">
          {t("create_new_password")}
        </h2>

        <form
          className="w-full"
          onSubmit={handleSubmit(onSubmit)}
          aria-labelledby="register-password-title"
        >
          <div className="mb-2">
            <GeneralInput
              type="password"
              name="Password"
              label={t("password")}
              placeholder={t("password")}
              required
            />
            {errors.Password && (
              <p className="text-body-text mt-1 text-sm" role="alert">
                {errors.Password.message}
              </p>
            )}
          </div>

          <div className="password-feedback-container mt-2 flex w-full gap-1">
            {[...Array(totalLines)].map((_, index) => (
              <div
                key={index}
                className={`h-1 w-1/3 rounded ${getLineColor(
                  index,
                  criteriaMetCount,
                )}`}
              ></div>
            ))}
          </div>

          <ul className="my-3 space-y-1 text-sm">
            <li
              className={getFeedbackTextColor(
                validatePasswordChars.isUpperCasePresent,
                Password,
              )}
            >
              {t("at_least_one_uppercase_letter")}
            </li>
            <li
              className={getFeedbackTextColor(
                validatePasswordChars.isNumberPresent,
                Password,
              )}
            >
              {t("must_contain_numbers")}
            </li>
            <li
              className={getFeedbackTextColor(
                validatePasswordChars.isPasswordLongEnough,
                Password,
              )}
            >
              {t("at_least_8_characters")}
            </li>
          </ul>

          {error && (
            <p
              className="text-body-text mb-4 text-center"
              role="alert"
              aria-live="assertive"
            >
              {errorMessage}
            </p>
          )}

          <GeneralButton
            type="submit"
            title={t("create_password")}
            color="blue"
            block
            disabled={!isPasswordValid || isPending}
            ariaLabel={t("verify")}
          />
          {isPending && <Loader />}
        </form>
      </AuthLayout>

      {isModalOpen && (
        <ConfirmationPopup
          isModalOpen={isModalOpen}
          title={t("all_info_will_lost")}
          description={t("are_you_sure_to_do_action")}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmGoBack}
          ariaLabel={t("confirm_navigation")}
        />
      )}
    </FormProvider>
  );
};

export default RegisterPassword;
